import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useVillain } from '../context/VillainContext';

const motivationalQuotes = [
  "While you're resting, your villain is training.",
  "Your villain doesn't wait for motivation. They act.",
  "Every excuse you make is a victory for your villain.",
  "Your villain embraces the pain of growth.",
  "Your villain is consistent when no one is watching.",
  "Your villain turns setbacks into comebacks.",
  "Your villain doesn't need perfect conditions to start.",
  "Your villain is building their empire while you're building excuses.",
  "Your villain doesn't fear failure; they learn from it.",
  "Your villain is disciplined every single day."
];

const VillainDashboard = ({ onEdit }) => {
  const { villain, resetVillain } = useVillain();

  const getRandomQuote = () => {
    return motivationalQuotes[Math.floor(Math.random() * motivationalQuotes.length)];
  };

  if (!villain.created) {
    return null;
  }

  return (
    <DashboardContainer>
      <DashboardHeader>
        <VillainName>{villain.name}</VillainName>
        <HeaderButtons>
          <EditButton onClick={onEdit}>Edit</EditButton>
          <ResetButton onClick={resetVillain}>Reset</ResetButton>
        </HeaderButtons>
      </DashboardHeader>

      <QuoteBox>
        <Quote>"{getRandomQuote()}"</Quote>
      </QuoteBox>

      <AttributesHeading>Your Villain's Attributes:</AttributesHeading>

      <AttributesList>
        {villain.attributes.filter(attr => attr).map((attribute, index) => (
          <AttributeItem key={index}>
            <AttributeNumber>{index + 1}</AttributeNumber>
            <AttributeText>{attribute}</AttributeText>
          </AttributeItem>
        ))}
      </AttributesList>

      <MotivationalText>
        Every time you make progress, you're catching up to {villain.name}.
        Every time you give up, they pull further ahead.
      </MotivationalText>
    </DashboardContainer>
  );
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const DashboardContainer = styled.div`
  background: linear-gradient(145deg, #1a1a1a, #0a0a0a);
  border-radius: 8px;
  padding: 2rem;
  margin: 1rem 0;
  box-shadow: 0 10px 30px rgba(170, 51, 51, 0.2);
  animation: ${css`${fadeIn} 0.5s ease-in-out`};
  max-width: 800px;
  width: 100%;
  border: 1px solid rgba(170, 51, 51, 0.3);
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #a33;
  padding-bottom: 1rem;
`;

const VillainName = styled.h3`
  font-family: var(--font-primary);
  font-size: 2rem;
  color: #a33;
  margin: 0;
  text-shadow: 0 0 10px rgba(170, 51, 51, 0.5);
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Button = styled.button`
  background: transparent;
  border: 1px solid;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  font-family: var(--font-secondary);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
`;

const EditButton = styled(Button)`
  border-color: #d4af37;
  color: #d4af37;

  &:hover {
    background: rgba(212, 175, 55, 0.1);
  }
`;

const ResetButton = styled(Button)`
  border-color: #a33;
  color: #a33;

  &:hover {
    background: rgba(170, 51, 51, 0.1);
  }
`;

const QuoteBox = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-left: 3px solid #d4af37;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border-radius: 0 4px 4px 0;
`;

const Quote = styled.p`
  font-family: var(--font-primary);
  font-style: italic;
  color: var(--color-text);
  font-size: 1rem;
  line-height: 1.5;
  margin: 0;
`;

const AttributesHeading = styled.h4`
  font-family: var(--font-secondary);
  color: var(--color-text);
  margin-bottom: 1rem;
  font-size: 1.1rem;
`;

const AttributesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem 0;
  max-height: 300px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: #111;
  }

  &::-webkit-scrollbar-thumb {
    background: #333;
    border-radius: 5px;
  }
`;

const AttributeItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 0.8rem;
  background: rgba(0, 0, 0, 0.2);
  padding: 0.8rem;
  border-radius: 4px;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateX(5px);
    background: rgba(0, 0, 0, 0.3);
  }
`;

const AttributeNumber = styled.span`
  background: #a33;
  color: #fff;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 0.8rem;
  flex-shrink: 0;
  font-size: 0.8rem;
  box-shadow: 0 0 5px rgba(170, 51, 51, 0.5);
`;

const AttributeText = styled.span`
  color: var(--color-text);
  font-family: var(--font-secondary);
  font-size: 0.95rem;
`;

const MotivationalText = styled.p`
  font-family: var(--font-secondary);
  color: #a33;
  text-align: center;
  font-style: italic;
  margin: 1.5rem 0 0 0;
  line-height: 1.5;
  text-shadow: 0 0 5px rgba(170, 51, 51, 0.3);
`;

export default VillainDashboard;
